import { useState, useEffect } from "react";
import { clearPersistedPageIndexes } from "../hooks/usePersistedState";
import axios from "axios";
import jsonrepair from "jsonrepair";
import { useAuthUser, useSignOut } from "react-auth-kit";
import {
  KeyboardDoubleArrowLeft,
  ExpandMore,
  ChevronRight,
  Menu,
  Logout,
  ManageAccounts,
  People,
  Settings,
  TimeToLeave,
  Topic,
  Assignment,
  Bookmark,
  CoPresent,
  MobiledataOff,
  StorefrontOutlined,
  SwapVert,
  Token,
} from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Avatar, Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import localize from "../common/localize";
import ApplicationLogoHorizontal from "./common/ApplicationLogoHorizontal";
import {
  RemoveFiltersFromLocalStorage,
  defaultFilteringForEvent,
  defaultFilteringForNewness,
} from "../common/EntitiesManagement";
import useGlobalContext from "../hooks/useGlobalContext";
import ModalBackground from "./common/ModalBackground";
import SignOut from "./common/SignOut";

const sidebarWidth = 280;

const DropdownMenuItem = ({
  title,
  link,
  setshowSideBar,
  icon,
  setEventFilters,
  setNewnessFilters,
}) => {
  const location = useLocation();
  const isActive = location.pathname === link;

  return (
    <Link
      to={link}
      onClick={() => {
        setEventFilters(defaultFilteringForEvent(true));
        setNewnessFilters(defaultFilteringForNewness(true));
        setshowSideBar(false);
      }}
      style={{ textDecoration: "none" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 0.8,
          px: 2,
          ml: 1,
          mr: 1,
          mb: 0.3,
          borderRadius: 2,
          fontSize: "0.8rem",
          color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
          backgroundColor: isActive ? "rgba(255,255,255,0.12)" : "transparent",
          transition: "all 0.15s ease",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.08)",
            color: "#fff",
          },
          marginLeft: "30px"
        }}
      >
        {icon && (
          <Box sx={{ display: "flex", "& .MuiSvgIcon-root": { fontSize: 16 } }}>
            {icon}
          </Box>
        )}
        <Typography sx={{ fontSize: "0.8rem", fontWeight: isActive ? 600 : 400 }}>
          {title}
        </Typography>
      </Box>
    </Link>
  );
};

const sectionTreeItemSx = {
  mb: 0.5,
  "& > .MuiTreeItem-content": {
    py: 1,
    px: 1.5,
    borderRadius: 2,
    mx: 0.5,
    transition: "background 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.06)",
    },
  },
  "& > .MuiTreeItem-content .MuiTreeItem-label": {
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "#fff",
  },
  "& > .MuiTreeItem-content .MuiTreeItem-iconContainer": {
    color: "rgba(255,255,255,0.5)",
  },
  "& .MuiCollapse-root": {
    ml: 0,
    pl: 0,
    borderLeft: "none",
  },
};

export default function Sidebar() {
  const { setFilterForEvents, setFilterForNewness, AUTH_ENDPOINT, config } =
    useGlobalContext();
  const [userRoles, setUserRoles] = useState(undefined);
  const auth = useAuthUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const userData = auth();
  const [showSidebar, setShowSidebar] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  useEffect(() => {
    if (userData) {
      setUserRoles(userData.UserRoles);
    }
  }, [userData]);

  const getModuleRestricted = (moduleNames, actionName = "ACCESS") => {
    if (userRoles && moduleNames?.length > 0) {
      try {
        const description = userRoles[0].Description;
        if (!description) return false;
        const modules = JSON.parse(jsonrepair(description));
        const restrictedModulesFound = moduleNames.map((moduleName) => {
          const mod = modules.find((m) => m.Form === moduleName);
          return mod?.Actions?.[actionName] === true;
        });
        return restrictedModulesFound.every(Boolean);
      } catch {
        const restrictedModulesFound = moduleNames.map((module) =>
          userRoles[0].Description?.match(
            `{Form: ${module}, Restriction: ACCESS}`
          )
        );
        const array = restrictedModulesFound.filter((x) => x != null);
        return array?.length === moduleNames?.length;
      }
    }

    return false;
  };

  const requestLogOut = async () => {
    await axios.put(
      `${AUTH_ENDPOINT}/logout/${userData?.SessionId}`,
      {},
      config
    );
  };

  const userInitials = userData?.UserName
    ? userData.UserName.substring(0, 2).toUpperCase()
    : "U";

  return (
    <>
      {showSignOutModal && (
        <ModalBackground
          okButtonText={localize.sign_out}
          title={localize.app_name}
          showModal={showSignOutModal}
          setShowModal={setShowSignOutModal}
          onClickToCancel={() => {
            setShowSignOutModal(false);
          }}
          onClickToSubmit={() => {
            setShowSignOutModal(false);
            navigate("/");
            requestLogOut();
            clearPersistedPageIndexes();
            signOut();
          }}
          Component={<SignOut />}
        />
      )}

      {!showSidebar && (
        <Tooltip title={localize.tooltip_open_menu}>
          <IconButton
            onClick={() => setShowSidebar(true)}
            sx={{ color: "white" }}
          >
            <Menu />
          </IconButton>
        </Tooltip>
      )}

      {showSidebar && (
        <Box
          onClick={() => setShowSidebar(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: 39,
            backdropFilter: "blur(2px)",
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: sidebarWidth,
          zIndex: 40,
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg, #0d2b5e 0%, #122f61 50%, #0a1f45 100%)",
          boxShadow: showSidebar ? "4px 0 24px rgba(0,0,0,0.3)" : "none",
          transform: showSidebar ? "translateX(0)" : `translateX(-${sidebarWidth + 10}px)`,
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.15)",
            borderRadius: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 2,
            position: "sticky",
            top: 0,
            zIndex: 1,
            background: "inherit",
          }}
        >
          <Link
            to="/"
            onClick={() => RemoveFiltersFromLocalStorage()}
            style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
          >
            <ApplicationLogoHorizontal height={20} width={110} />
          </Link>
          <Tooltip title={localize.tooltip_close_menu}>
            <IconButton
              onClick={() => setShowSidebar(false)}
              sx={{
                color: "rgba(255,255,255,0.6)",
                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
              }}
              size="small"
            >
              <KeyboardDoubleArrowLeft fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2 }} />

        <Box sx={{ flex: 1, py: 1.5 }}>
          <TreeView
            defaultCollapseIcon={<ExpandMore sx={{ color: "rgba(255,255,255,0.5)" }} />}
            defaultExpandIcon={<ChevronRight sx={{ color: "rgba(255,255,255,0.5)" }} />}
          >
            {!getModuleRestricted([
              "OPERATIVE_AREA",
              "EVENT_TYPE",
              "FORM_FIELD",
              "DYNAMIC_FORM",
              "EXECUTION_STATUS",
              "WORKFLOW",
              "ADDITIONAL_NEWNESS",
              "ADDITIONAL_COST_TYPE",
              "PAYMENT_TYPE",
              "EVIDENCE_TEMPLATE",
              "REPORT_TYPE",
              "REQUEST_TYPE", 
              "FINISH_CONCEPT"
            ], "ACCESS") && (
                <TreeItem
                  sx={sectionTreeItemSx}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Settings sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                      {localize.main_menu_process_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_process_management}
                >
                  {!getModuleRestricted(["OPERATIVE_AREA"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.operational_areas}
                      link="/operational-area"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["EVENT_TYPE"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.event_type_title}
                      link="/eventtype"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["FORM_FIELD"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.form_fields}
                      link="/formfields"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["DYNAMIC_FORM"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.dynamic_forms}
                      link="/dynamicforms"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["EXECUTION_STATUS"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.execution_statuses}
                      link="/status"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["WORKFLOW"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.workflows}
                      link="/workflow"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["ADDITIONAL_NEWNESS"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.additionalnewnesssetup}
                      link="/additionalnewness"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["ADDITIONAL_COST_TYPE"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.additional_cost_type_title}
                      link="/additionalCostType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["PAYMENT_TYPE"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.payment_type_title}
                      link="/paymentType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["EVIDENCE_TEMPLATE"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.evidence_template}
                      link="/evidencetemplates"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["REPORT_TYPE"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.report_types}
                      link="/reportType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["REQUEST_TYPE"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.request_types}
                    link="/requestType"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
                  {!getModuleRestricted(["FINISH_CONCEPT"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.finish_concept}
                      link="/finishConcept"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

            {!getModuleRestricted([
              "EVENT",
              "DAILY_ROUTE",
              "PRE_ORDER",
              "REPORTED_NEWNESS",
              "REGISTER_PAYMENTS",
              "RELATED_BILLS",
              "SETTLEMENT",

            ], "ACCESS") && (
                <TreeItem
                  sx={sectionTreeItemSx}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Assignment sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                      {localize.main_menu_event_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_event_management}
                >
                  {!getModuleRestricted(["EVENT"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.events}
                      link="/events"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["DAILY_ROUTE"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.daily_route_title}
                      link="/dailyRoute"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["PRE_ORDER"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.pre_orders_title}
                      link="/preorder"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["REPORTED_NEWNESS"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.reported_newness}
                      link="/newness"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["REGISTER_PAYMENTS"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.register_payment}
                      link="/registerpayments"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["RELATED_BILLS"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.related_bills}
                      link="/bills"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["SETTLEMENT"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.settlements_title}
                      link="/settlement"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}

                </TreeItem>
              )}

            {!getModuleRestricted([
            "GENERAL_REQUESTS", 
            "ADDITIONAL_COST_REQUEST",
            "SUPPLY_REQUEST",
            "PAYMENT_TO_TECHNICIAN_REQUEST"], "ACCESS") && (
              <TreeItem
                sx={sectionTreeItemSx}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Bookmark sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                    {localize.main_menu_general_request}
                  </Box>
                }
                nodeId={localize.main_menu_general_request}
              >
                
                {!getModuleRestricted(["GENERAL_REQUESTS"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.general_requests}
                    link="/generalRequests"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
                {!getModuleRestricted(["ADDITIONAL_COST_REQUEST"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.additional_cost_request_menu}
                    link="/additionalcostrequest"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
                {!getModuleRestricted(["SUPPLY_REQUEST"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.supply_request_menu}
                      link="/supplyrequest"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                {!getModuleRestricted(
                  ["PAYMENT_TO_TECHNICIAN_REQUEST"],
                  "ACCESS",
                ) && (
                  <DropdownMenuItem
                    title={localize.payment_to_technician_request_menu}
                    link="/paymenttotechnicianrequest"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
              </TreeItem>
            )}

            {!getModuleRestricted(["CONTRACTOR"], "ACCESS") && (
              <TreeItem
                sx={sectionTreeItemSx}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <StorefrontOutlined sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                    {localize.main_menu_contractor_management}
                  </Box>
                }
                nodeId={localize.main_menu_contractor_management}
              >
                {!getModuleRestricted(["CONTRACTOR"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.contractors}
                    link="/contractor"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
              </TreeItem>
            )}

            {!getModuleRestricted([
              "POSITION",
              "PERSONAL",
            ], "ACCESS") && (
                <TreeItem
                  sx={sectionTreeItemSx}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <People sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                      {localize.main_menu_personal_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_personal_management}
                >
                  {!getModuleRestricted(["POSITION"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.positions}
                      link="/position"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["PERSONAL"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.personal}
                      link="/personal"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

            {!getModuleRestricted([
              "MEASUREMENT_UNIT",
              "SUPPLY",
              "PROVIDER",
              
            ], "ACCESS") && (
                <TreeItem
                  sx={sectionTreeItemSx}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Token sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                      {localize.main_menu_supply_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_supply_management}
                >
                  {!getModuleRestricted(["MEASUREMENT_UNIT"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.measurement_units}
                      link="/measurementunit"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["SUPPLY"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.supplies}
                      link="/supply"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["PROVIDER"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.providers}
                      link="/provider"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  

                </TreeItem>
              )}

            {!getModuleRestricted([
              "INVENTORY_MOVEMENT_TYPE",
              "INVENTORY_MOVEMENT",
            ], "ACCESS") && (
                <TreeItem
                  sx={sectionTreeItemSx}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <SwapVert sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                      {localize.main_menu_inventory_management}
                    </Box>
                  }
                  nodeId={localize.main_menu_inventory_management}
                >
                  {!getModuleRestricted(["INVENTORY_MOVEMENT_TYPE"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.inventory_movement_type}
                      link="/inventoryMovementType"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                  {!getModuleRestricted(["INVENTORY_MOVEMENT"], "ACCESS") && (
                    <DropdownMenuItem
                      title={localize.inventory_movement_title}
                      link="/inventoryMovement"
                      setshowSideBar={setShowSidebar}
                      setEventFilters={setFilterForEvents}
                      setNewnessFilters={setFilterForNewness}
                    />
                  )}
                </TreeItem>
              )}

            {!getModuleRestricted(["CLIENT"], "ACCESS") && (
              <TreeItem
                sx={sectionTreeItemSx}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CoPresent sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                    {localize.main_menu_client_management}
                  </Box>
                }
                nodeId={localize.main_menu_client_management}
              >
                {!getModuleRestricted(["CLIENT"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.clients}
                    link="/client"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
              </TreeItem>
            )}

            {!getModuleRestricted(["PROCEEDING", "RESOLUTION"], "ACCESS") && (
              <TreeItem
                sx={sectionTreeItemSx}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Topic sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                    {localize.main_menu_proceedings}
                  </Box>
                }
                nodeId={localize.main_menu_proceedings}
              >
                {!getModuleRestricted(["PROCEEDING"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.proceedings}
                    link="/proceedings"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
                {!getModuleRestricted(["RESOLUTION"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.resolutions}
                    link="/resolutions"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
              </TreeItem>
            )}

            {!getModuleRestricted(["VEHICLE_TYPE", "VEHICLE"], "ACCESS") && (
              <TreeItem
                sx={sectionTreeItemSx}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <TimeToLeave sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                    {localize.main_menu_vehicle_management}
                  </Box>
                }
                nodeId={localize.main_menu_vehicle_management}
              >
                {!getModuleRestricted(["VEHICLE_TYPE"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.vehicle_types}
                    link="/vehicletypes"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
                {!getModuleRestricted(["VEHICLE"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.vehicles}
                    link="/vehicles"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
              </TreeItem>
            )}

            {!getModuleRestricted(["PROFILE", "USER"], "ACCESS") && (
              <TreeItem
                sx={sectionTreeItemSx}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ManageAccounts sx={{ fontSize: 18, color: "rgba(255,255,255,0.7)" }} />
                    {localize.main_menu_user_management}
                  </Box>
                }
                nodeId={localize.main_menu_user_management}
              >
                {!getModuleRestricted(["PROFILE"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.profiles}
                    link="/profiles"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
                {!getModuleRestricted(["USER"], "ACCESS") && (
                  <DropdownMenuItem
                    title={localize.users}
                    link="/users"
                    setshowSideBar={setShowSidebar}
                    setEventFilters={setFilterForEvents}
                    setNewnessFilters={setFilterForNewness}
                  />
                )}
              </TreeItem>
            )}
          </TreeView>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            background: "inherit",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            px: 2,
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                }}
              >
                {userInitials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 140,
                  }}
                >
                  {userData?.UserName ?? ""}
                </Typography>
                <Typography sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)" }}>
                  {localize.version}
                </Typography>
              </Box>
            </Box>
            <Tooltip title={localize.tooltip_logout}>
              <IconButton
                onClick={() => setShowSignOutModal(true)}
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  "&:hover": { color: "#ff6b6b", backgroundColor: "rgba(255,107,107,0.1)" },
                }}
                size="small"
              >
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </>
  );
}
